import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import GradesValueToColorHelper from '../Helpers/GradesValueToColorHelper';

const GRADE_ROWS = [
	{ key: 'offensive_line_grade', label: 'O-Line' },
	{ key: 'quarterbacks_grade', label: 'QB' },
	{ key: 'wide_receivers_grade', label: 'WR' },
	{ key: 'running_backs_grade', label: 'RB' },
	{ key: 'tight_ends_grade', label: 'TE' },
	{ key: 'head_coach_grade', label: 'HC' },
	{ key: 'offensive_coordinator_grade', label: 'OC' }
];

const MAX_GRADE = 10;

function formatGameDate(gameDate) {
	if (!gameDate) return 'Date TBD';

	// Date-only strings (e.g. "2026-09-13") are parsed as UTC midnight by
	// `new Date()`, which can shift the displayed day west of UTC. Parse the
	// parts manually so the date is interpreted in the user's local time.
	const isoDateMatch =
		typeof gameDate === 'string' && gameDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	const date = isoDateMatch
		? new Date(
				Number(isoDateMatch[1]),
				Number(isoDateMatch[2]) - 1,
				Number(isoDateMatch[3])
			)
		: new Date(gameDate);
	if (Number.isNaN(date.getTime())) return 'Date TBD';

	return date.toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric'
	});
}

function teamLogoSrc(imageUrl, size = 64) {
	if (!imageUrl) return null;
	return `${imageUrl}?w=${size}&h=${size}`;
}

function GradeDuelRow({ label, leftTeam, rightTeam, gradeKey }) {
	const leftValue = Number(leftTeam[gradeKey]) || 0;
	const rightValue = Number(rightTeam[gradeKey]) || 0;
	const leftWidth = Math.max(4, (leftValue / MAX_GRADE) * 100);
	const rightWidth = Math.max(4, (rightValue / MAX_GRADE) * 100);
	const leftColor = GradesValueToColorHelper(leftValue);
	const rightColor = GradesValueToColorHelper(rightValue);

	return (
		<div className="space-y-1">
			<div className="flex items-center justify-between text-2xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
				<span className={leftValue > rightValue ? 'font-black text-gray-900 dark:text-dark-text' : 'font-medium text-gray-500 dark:text-gray-400'}>
					{leftValue.toFixed(1)}
				</span>
				<span className="font-semibold">{label}</span>
				<span className={rightValue > leftValue ? 'font-black text-gray-900 dark:text-dark-text' : 'font-medium text-gray-500 dark:text-gray-400'}>
					{rightValue.toFixed(1)}
				</span>
			</div>
			<div className="flex items-center gap-2">
				<div className="flex-1 flex justify-end h-2 rounded-full bg-gray-100 dark:bg-dark-bg overflow-hidden">
					<div
						className="h-full rounded-full transition-all"
						style={{ width: `${leftWidth}%`, backgroundColor: leftColor }}
					/>
				</div>
				<div className="w-px h-4 bg-gray-300 dark:bg-dark-border flex-shrink-0" />
				<div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-dark-bg overflow-hidden">
					<div
						className="h-full rounded-full transition-all"
						style={{ width: `${rightWidth}%`, backgroundColor: rightColor }}
					/>
				</div>
			</div>
		</div>
	);
}

function buildBestBallAngle(leftTeam, rightTeam) {
	const edges = GRADE_ROWS.map(({ key, label }) => {
		const leftValue = Number(leftTeam[key]) || 0;
		const rightValue = Number(rightTeam[key]) || 0;
		const delta = leftValue - rightValue;
		return { label, delta };
	}).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

	const topEdge = edges[0];
	if (!topEdge || Math.abs(topEdge.delta) < 0.3) {
		return 'Offensive environments look evenly matched on paper — lean on your stack correlation and ADP value.';
	}

	if (topEdge.delta > 0) {
		return `${leftTeam.abbreviation} holds the edge in ${topEdge.label} (+${topEdge.delta.toFixed(1)}). Favor their offensive pieces for ceiling weeks.`;
	}

	return `${rightTeam.abbreviation} holds the edge in ${topEdge.label} (+${Math.abs(topEdge.delta).toFixed(1)}). Their bring-backs may outperform in this spot.`;
}

export default function ChampionshipMatchupCompareModal({
	isOpen,
	onClose,
	team,
	opponent,
	week,
	matchup
}) {
	useEffect(() => {
		if (!isOpen) return undefined;

		const handleEscape = (event) => {
			if (event.key === 'Escape') onClose();
		};

		document.body.style.overflow = 'hidden';
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isOpen, onClose]);

	if (!isOpen || !team || !opponent || !matchup) {
		return null;
	}

	const homeTeam = matchup.is_home_game ? team : opponent;
	const awayTeam = matchup.is_home_game ? opponent : team;
	const leftTeam = awayTeam;
	const rightTeam = homeTeam;
	const bestBallAngle = buildBestBallAngle(leftTeam, rightTeam);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-6"
			onClick={onClose}
			role="presentation"
		>
			<div
				className="relative w-full max-w-xl rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-xl max-h-full overflow-y-auto"
				onClick={(event) => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="championship-matchup-compare-title"
			>
				<button
					type="button"
					onClick={onClose}
					className="absolute top-3 right-3 rounded-full p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-bg"
					aria-label="Close matchup compare"
				>
					<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>

				<div className="p-5 sm:p-6">
					<div className="text-center">
						<div className="text-2xs font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">
							Week {week} · Championship Weeks
						</div>
						<h2
							id="championship-matchup-compare-title"
							className="mt-1 text-lg font-bold text-gray-900 dark:text-dark-text"
						>
							{formatGameDate(matchup.game_date)}
						</h2>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							{awayTeam.abbreviation} away @ {homeTeam.abbreviation} home
						</p>
					</div>

					<div className="mt-5 flex items-center justify-between gap-3">
						<div className="flex-1 text-center min-w-0">
							{teamLogoSrc(leftTeam.image_url) && (
								<img
									src={teamLogoSrc(leftTeam.image_url)}
									alt={`${leftTeam.name} logo`}
									className="mx-auto h-16 w-16 object-contain"
								/>
							)}
							<div className="mt-2 text-base font-bold text-gray-900 dark:text-dark-text">
								{leftTeam.abbreviation}
							</div>
							<div className="text-2xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
								Away
							</div>
							<div className="text-2xs text-gray-500 dark:text-gray-400 truncate px-1">
								{leftTeam.conference} · {leftTeam.division}
							</div>
							{leftTeam.w_l_odds && (
								<div className="mt-1 text-2xs font-semibold text-gray-600 dark:text-gray-300">
									O/U {leftTeam.w_l_odds}
								</div>
							)}
							<Link
								to={`/teams/${leftTeam.id}`}
								className="mt-2 inline-block text-2xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								Team profile
							</Link>
						</div>

						<div className="flex-shrink-0 px-2">
							<div className="rounded-full border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-3 py-1 text-sm font-black text-gray-700 dark:text-gray-200">
								{matchup.is_home_game ? 'VS' : '@'}
							</div>
						</div>

						<div className="flex-1 text-center min-w-0">
							{teamLogoSrc(rightTeam.image_url) && (
								<img
									src={teamLogoSrc(rightTeam.image_url)}
									alt={`${rightTeam.name} logo`}
									className="mx-auto h-16 w-16 object-contain"
								/>
							)}
							<div className="mt-2 text-base font-bold text-gray-900 dark:text-dark-text">
								{rightTeam.abbreviation}
							</div>
							<div className="text-2xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
								Home
							</div>
							<div className="text-2xs text-gray-500 dark:text-gray-400 truncate px-1">
								{rightTeam.conference} · {rightTeam.division}
							</div>
							{rightTeam.w_l_odds && (
								<div className="mt-1 text-2xs font-semibold text-gray-600 dark:text-gray-300">
									O/U {rightTeam.w_l_odds}
								</div>
							)}
							<Link
								to={`/teams/${rightTeam.id}`}
								className="mt-2 inline-block text-2xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								Team profile
							</Link>
						</div>
					</div>

					<div className="mt-6 rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4 space-y-4">
						<div className="flex items-center justify-between text-2xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
							<span>{leftTeam.abbreviation}</span>
							<span>Offensive environment</span>
							<span>{rightTeam.abbreviation}</span>
						</div>
						{GRADE_ROWS.map((row) => (
							<GradeDuelRow
								key={row.key}
								label={row.label}
								gradeKey={row.key}
								leftTeam={leftTeam}
								rightTeam={rightTeam}
							/>
						))}
					</div>

					<div className="mt-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 p-3">
						<div className="text-2xs font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300">
							Best Ball angle
						</div>
						<p className="mt-1 text-xs leading-relaxed text-blue-900 dark:text-blue-100">
							{bestBallAngle}
						</p>
					</div>

					<div className="mt-4 grid grid-cols-2 gap-3 text-xs">
						<div className="rounded-lg border border-gray-200 dark:border-dark-border p-3">
							<div className="font-semibold text-gray-900 dark:text-dark-text">{leftTeam.abbreviation} staff</div>
							<div className="mt-1 text-gray-600 dark:text-gray-400">
								<span className="font-semibold uppercase tracking-wide text-2xs text-gray-500 dark:text-gray-500 mr-1">HC</span>
								{leftTeam.head_coach || '—'}
							</div>
							<div className="text-gray-600 dark:text-gray-400">
								<span className="font-semibold uppercase tracking-wide text-2xs text-gray-500 dark:text-gray-500 mr-1">OC</span>
								{leftTeam.offensive_coordinator || '—'}
							</div>
						</div>
						<div className="rounded-lg border border-gray-200 dark:border-dark-border p-3">
							<div className="font-semibold text-gray-900 dark:text-dark-text">{rightTeam.abbreviation} staff</div>
							<div className="mt-1 text-gray-600 dark:text-gray-400">
								<span className="font-semibold uppercase tracking-wide text-2xs text-gray-500 dark:text-gray-500 mr-1">HC</span>
								{rightTeam.head_coach || '—'}
							</div>
							<div className="text-gray-600 dark:text-gray-400">
								<span className="font-semibold uppercase tracking-wide text-2xs text-gray-500 dark:text-gray-500 mr-1">OC</span>
								{rightTeam.offensive_coordinator || '—'}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
